/**
 * Delivery Date Handler
 * Gère le calcul et l'affichage des dates de livraison estimées
 * pour les pages collection, produit et panier
 */

(function() {
  'use strict';

  const DEBUG = false; // Mettre à false pour désactiver les logs

  function log(message, data) {
    if (DEBUG) {
      console.log('[DeliveryDateHandler]', message, data || '');
    }
  }

  // ============================================
  // UTILITAIRES
  // ============================================

  // Liste des jours fériés sous forme de chaînes de caractères au format 'MM-DD'
  const fixedHolidays = [
    '01-01', // Jour de l'An
    '05-01', // Fête du Travail
    '05-08', // Victoire des Alliés 1945
    '07-14', // Fête Nationale
    '08-15', // Assomption
    '11-01', // Toussaint
    '11-11', // Armistice 1918
    '12-25', // Noël
  ];

  /**
   * Vérifie si une date est un jour férié
   */
  function isHoliday(date) {
    const monthDay = date.toISOString().slice(5, 10);
    return fixedHolidays.includes(monthDay);
  }

  /**
   * Ajoute un nombre de jours ouvrés à une date (exclut weekends et jours fériés)
   */
  function addBusinessDays(date, daysToAdd) {
    let newDate = new Date(date);
    let businessDaysLeft = daysToAdd;
    let dayOfWeek;
    let iterations = 0;
    const maxIterations = 100; // Sécurité pour éviter les boucles infinies

    while (businessDaysLeft > 0 && iterations < maxIterations) {
      iterations++;
      newDate.setDate(newDate.getDate() + 1);
      dayOfWeek = newDate.getDay();

      // Si ce n'est pas un week-end et pas un jour férié, décrémente le compteur
      if (dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday(newDate)) {
        businessDaysLeft--;
      }
    }

    if (iterations >= maxIterations) {
      console.warn('WARNING: maxIterations reached in addBusinessDays', { daysToAdd, finalDate: newDate.toISOString() });
    }

    return newDate;
  }

  /**
   * Formate une fourchette de dates pour l'affichage
   * @param {Date} minDate - Date minimale
   * @param {Date} maxDate - Date maximale
   * @returns {string} Texte formaté (ex: "entre le 23 et le 26 janvier" ou "entre le 23 janvier et le 2 février")
   */
  function formatDeliveryDateRange(minDate, maxDate, locale) {
    const options = { day: 'numeric', month: 'long' };
    const formattedMinDate = minDate.toLocaleDateString(locale, options);
    const formattedMaxDate = maxDate.toLocaleDateString(locale, options);
    const monthName = maxDate.toLocaleDateString(locale, { month: 'long' });

    const strings = window.themeStrings || {};

    // Vérifier si les deux dates sont dans le même mois
    if (minDate.getMonth() === maxDate.getMonth()) {
      const template = strings.deliveryBetweenSameMonth || 'entre le {{min}} et le {{max}} {{month}}';
      return template
        .replace('{{min}}', minDate.getDate())
        .replace('{{max}}', maxDate.getDate())
        .replace('{{month}}', monthName);
    } else {
      const template = strings.deliveryBetweenDiffMonth || 'entre le {{min_date}} et le {{max_date}}';
      return template
        .replace('{{min_date}}', formattedMinDate)
        .replace('{{max_date}}', formattedMaxDate);
    }
  }

  /**
   * Calcule une fourchette de dates de livraison à partir d'un nombre de jours
   * Applique la logique J-1 / J+1
   * @param {number} days - Nombre de jours
   * @param {Date} [startDate] - Date de départ (par défaut: aujourd'hui)
   * @returns {Object} {minDate, maxDate, displayText}
   */
  function calculateDeliveryDateRange(days, startDate, locale) {
    const orderDate = startDate || new Date();
    const minDays = Math.max(1, days - 2); // J-2, minimum 1 jour
    const maxDays = days + 1; // J+1

    const minDate = addBusinessDays(orderDate, minDays);
    const maxDate = addBusinessDays(orderDate, maxDays);
    const displayText = formatDeliveryDateRange(minDate, maxDate, locale);

    return {
      minDate,
      maxDate,
      displayText
    };
  }

  // ============================================
  // LOGIQUE POUR PAGES COLLECTION
  // ============================================

  /**
   * Extrait le nombre de jours depuis un texte
   * Exemples: "entre 5 et 7 jours ouvrés" -> {min: 5, max: 7}, "5 jours ouvrés" -> {min: 5, max: null}
   */
  function extractDaysFromText(text) {
    if (!text) {
      return null;
    }
    
    const daysMatch = text.match(/(\d+)\s*(?:et\s*(\d+))?\s*jours?/i);
    
    if (!daysMatch) {
      return null;
    }

    const min = parseInt(daysMatch[1]);
    
    if (daysMatch[2]) {
      // Si on trouve "entre X et Y", retourner min et max
      const max = parseInt(daysMatch[2]);
      return { min, max, isRange: true };
    } else {
      // Sinon prendre le premier nombre trouvé
      return { min, max: null, isRange: false };
    }
  }

  /**
   * Initialise le calcul des dates de livraison pour les pages collection
   */
  function initCollectionDeliveryDate() {
    const deliveryElements = document.querySelectorAll('.js-jours-collection');
    
    if (deliveryElements.length === 0) {
      return;
    }

    const orderDate = new Date();
    const locale = document.documentElement.lang || 'fr';

    deliveryElements.forEach(function(element) {
      const deliveryText = element.getAttribute('data-delivery-text');

      if (!deliveryText) {
        return;
      }

      // Vérifier si le texte contient "Se référer à chaque fiche produit"
      if (deliveryText.includes('Se référer à chaque fiche produit') ||
          deliveryText.includes('référer à chaque fiche produit')) {
        return;
      }

      // Extraire le nombre de jours depuis le texte
      const daysInfo = extractDaysFromText(deliveryText);

      if (daysInfo && daysInfo.min) {
        if (daysInfo.isRange && daysInfo.max) {
          // Cas d'une fourchette : calculer les deux dates
          const minDate = addBusinessDays(orderDate, daysInfo.min);
          const maxDate = addBusinessDays(orderDate, daysInfo.max);
          const displayText = formatDeliveryDateRange(minDate, maxDate, locale);
          element.textContent = displayText;
        } else {
          // Cas d'un seul nombre : appliquer J-1 / J+1
          const result = calculateDeliveryDateRange(daysInfo.min, orderDate, locale);
          element.textContent = result.displayText;
        }
      }
    });
  }

  // ============================================
  // LOGIQUE POUR PAGES PRODUIT
  // ============================================

  /**
   * Initialise le calcul des dates de livraison pour les pages produit
   */
  function initProductDeliveryDate() {
    if (window.location.href.indexOf('/products/') > -1) {
      const dayElement = document.querySelector('.js-jours');
      if (dayElement) {
        const day = dayElement.getAttribute('day');
        if (day) {
          const dayNum = parseInt(day);
          const locale = document.documentElement.lang || 'fr';
          const result = calculateDeliveryDateRange(dayNum, null, locale);
          dayElement.textContent = result.displayText;
        }
      }
    }
  }

  // ============================================
  // LOGIQUE POUR PANIER
  // ============================================

  /**
   * Initialise le calcul des dates de livraison pour le panier
   * Traite à la fois les items individuels (page panier) et les headers de colis (drawer)
   */
  function initCartDeliveryDate() {
    const perfStart = performance.now();

    // Déterminer si on est dans le cart drawer
    const isDrawer = !!document.getElementById('cart__drawer');

    let itemsProcessed = 0;

    const locale = document.documentElement.lang || 'fr';

    // Sur la page panier classique (pas le drawer), on garde le calcul par item
    if (!isDrawer) {
    const cartItems = document.querySelectorAll('.cart__item--container');
    cartItems.forEach(function(item) {
      const dayElement = item.querySelector('.js-jours-cart');
      if (dayElement) {
        const day = dayElement.getAttribute('day');
        if (day) {
          const dayNum = parseInt(day);
          const result = calculateDeliveryDateRange(dayNum, null, locale);
          dayElement.textContent = result.displayText;
            itemsProcessed++;
        }
      }
    });
    }

    // Headers de colis (drawer) – coût très faible
    const colisHeaders = document.querySelectorAll('.colis-header .js-jours-cart');
    let headersProcessed = 0;
    colisHeaders.forEach(function(element) {
      const day = element.getAttribute('day');
      if (day) {
        const dayNum = parseInt(day);
        const result = calculateDeliveryDateRange(dayNum, null, locale);
        element.textContent = result.displayText;
        headersProcessed++;
      }
    });
    
    const duration = performance.now() - perfStart;
    if (window.CartEventManager && window.CartEventManager.PerformanceLogger) {
      window.CartEventManager.PerformanceLogger.log('Delivery dates calculated', {
        items: itemsProcessed,
        headers: headersProcessed,
        duration: `${duration.toFixed(2)}ms`
      });
    }
  }

  /**
   * Fonction pour mettre à jour les dates de livraison dans le panier
   * Appelée après les mises à jour dynamiques du panier
   */
  function updateCartDeliveryDates() {
    // Plus de setTimeout : le DOM est déjà prêt grâce à CartEventManager / renderCart
      initCartDeliveryDate();
  }

  // ============================================
  // INITIALISATION
  // ============================================

  /**
   * Initialise toutes les fonctionnalités de dates de livraison
   */
  function init() {
    // Initialiser les pages collection
    initCollectionDeliveryDate();

    // Initialiser les pages produit
    initProductDeliveryDate();

    // Initialiser le panier
    initCartDeliveryDate();

    // Écouter les mises à jour du panier (si jQuery est disponible)
    // NOTE: Désactivé car CartEventManager gère déjà les appels via cart:data:ready
    // L'événement jQuery cart:updated est redondant et cause un double appel
    // CartEventManager appelle updateCartDeliveryDates() au bon moment dans le flux
    /*
    if (typeof $ !== 'undefined') {
      // Écouter les événements de mise à jour du panier
      $(document).on('cart:updated', function() {
        updateCartDeliveryDates();
      });
    }
    */
  }

  // Initialiser au chargement du DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      init();
    });
  } else {
    init();
  }

  // Exposer les fonctions pour utilisation externe (notamment depuis theme-apm.js)
  window.DeliveryDateHandler = {
    init: init,
    initCollectionDeliveryDate: initCollectionDeliveryDate,
    initProductDeliveryDate: initProductDeliveryDate,
    initCartDeliveryDate: initCartDeliveryDate,
    updateCartDeliveryDates: updateCartDeliveryDates,
    calculateDeliveryDateRange: calculateDeliveryDateRange,
    formatDeliveryDateRange: formatDeliveryDateRange,
    addBusinessDays: addBusinessDays,
    isHoliday: isHoliday
  };

  // Compatibilité avec l'ancien système (pour theme-apm.js)
  window.DeliveryDateUtils = {
    isHoliday: isHoliday,
    addBusinessDays: addBusinessDays,
    formatDeliveryDateRange: formatDeliveryDateRange,
    calculateDeliveryDateRange: calculateDeliveryDateRange
  };
})();
